import React, { FC, useEffect, useMemo, useState } from 'react'

import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import allLocales from '@fullcalendar/core/locales-all'

import { Box } from '@mui/material'
import { addMinutes, format } from 'date-fns'

import { useGET_meetingsQuery } from 'redux/api/meetingsApi'
import { useGET_milestonesQuery } from 'redux/api/milestonesApi'
import { useGET_groupCourseSessionsQuery } from 'redux/api/sessionsApi'
import i18n from 'i18n'
import { MAX_PER_PAGE } from 'utils/constants'

import styled from "@emotion/styled";
import { removeEmpty } from 'utils/helpers'

interface IProps {
  filters?: { [key: string]: string | number | any[] }
  loadSessions?: boolean
  loadMeetings?: boolean
  loadMilestones?: boolean
}



const Calendar: FC<IProps> = ({filters = {}, loadSessions = false, loadMeetings = false, loadMilestones = false}) => {

  const getDate = (isAllDayEvent, date) => {
    return isAllDayEvent ? format(new Date(date), 'yyyy-MM-dd') : new Date(date)
  }
  const getEndTime = (date, duration) => {
    return addMinutes(new Date(date), duration)
  }

  const calendarSessions = (fetchInfo, successCallback, failureCallback) => {

    const qry = new URLSearchParams({ 
      page: '1', 
      page_size: MAX_PER_PAGE.toString(), 
      ordering: 'start_datetime', 
      start_date_from: format(fetchInfo.start, 'yyyy-MM-dd'),
      start_date_to: format(fetchInfo.end, 'yyyy-MM-dd'),
      ...removeEmpty(filters) 
    }).toString();

    fetch(`${process.env.REACT_APP_BASE_URL}/group-course-sessions/?${qry}`,   
      {
        headers: new Headers({
            'Authorization': `Token ${window.localStorage.getItem('token')}`, 
            'Content-Type': 'application/json'
        })
      }
    )   
     .then((response) => { 
      response.json().then((data) => {  
          successCallback(data.results.map((session) => { return {
            groupId: 'session',
            title: `${session.subject?.title ?? 'Session'}`,
            start: getDate(false, session.start_datetime),
            end: session.duration_minutes ? getEndTime(session.start_datetime, session.duration_minutes) : null,
            color: session.subject?.color_hex_code ?? '#ccc',
            textColor: '#000'
          }
        }));
      }).catch((err) => {
        successCallback([]);
      })
    });

  }

  const calendarMeetings = (fetchInfo, successCallback, failureCallback) => {

      const qry = new URLSearchParams({ 
        page: '1', 
        page_size: MAX_PER_PAGE.toString(), 
        ordering: 'start_datetime', 
        start_date_from: format(fetchInfo.start, 'yyyy-MM-dd'),
        start_date_to: format(fetchInfo.end, 'yyyy-MM-dd'),
        ...filters 
      }).toString();

      fetch(`${process.env.REACT_APP_BASE_URL}/meetings/?${qry}`,   
        {
          headers: new Headers({
              'Authorization': `Token ${window.localStorage.getItem('token')}`, 
              'Content-Type': 'application/json'
          })
        }
      )   
       .then((response) => { 
        response.json().then((data) => {  
            successCallback(data.results.map((meeting) => { return {
              groupId: 'meeting',
              title: meeting.purpose,
              start: getDate(false, meeting.start_datetime),
              end: meeting.duration_minutes ? getEndTime(meeting.start_datetime, meeting.duration_minutes) : null,
              color: '#000',
              textColor: '#84dbd5'
            }}));
        }).catch((err) => {
            successCallback([]);
        })
      });
  }

  const calendarMilestones = (fetchInfo, successCallback, failureCallback) => {

        const qry = new URLSearchParams({ 
          page: '1', 
          page_size: MAX_PER_PAGE.toString(), 
          ordering: 'start_datetime', 
          start_date_from: format(fetchInfo.start, 'yyyy-MM-dd'),
          start_date_to: format(fetchInfo.end, 'yyyy-MM-dd'),
          ...filters 
        }).toString();

        fetch(`${process.env.REACT_APP_BASE_URL}/milestones/?${qry}`,   
          {
            headers: new Headers({
                'Authorization': `Token ${window.localStorage.getItem('token')}`, 
                'Content-Type': 'application/json'
            })
          }
        )   
         .then((response) => { 
          response.json().then((data) => {  
            successCallback(data.results.map((milestone) => { return {
              groupId: 'milestone',
              title: milestone.title,
              start: getDate(true, milestone.start_date),
              end: getDate(true, milestone.end_date),
              color: '#de8aca',
              textColor: '#000'
            }}));
          }).catch((err) => {
              successCallback([]);
          })
        });
  };


  const StyleWrapper = styled.div`
  .fc td, .fc th, .fc table, .fc tr{
    background: #fefae4;
    border: none;
  }

  .fc-col-header-cell-cushion {
    color: #000;
  }

  .fc-timegrid-axis-cushion, .fc-timegrid-slot-label-cushion{
    color: #258D61;
  }

  .fc-daygrid-day-frame{
    padding: 5px;
  }

  .fc-event-main-frame{
    padding: 2px;
  }
    
  .fc-event-time{
    order: 10;
    font-weight: 600;
  }

  .fc-event-title {
    font-size: 12px;
    line-height: 100%;
  }
  `

  return (
    <Box sx={{ width: '100%', minWidth: '100%', background: '#fefae4' }}><StyleWrapper>
      <FullCalendar
        locales={allLocales} 
        locale={i18n.language}
        plugins={[dayGridPlugin,timeGridPlugin,interactionPlugin]}
        initialView='timeGridWeek'
        height='auto'
        dayMaxEventRows={true}
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay"
        }}
        views={{
          timeGrid: {
            dayMaxEventRows: 3,
          },
          dayGrid: {
            dayMaxEventRows: 3,
          },
        }}
        slotMinTime='06:00:00'
        themeSystem='Simplex'
        eventSources={[calendarSessions, calendarMeetings, calendarMilestones]}
        weekends={true}
      /></StyleWrapper>
    </Box>
  )
}

export default Calendar
